import React, { useMemo, useEffect, useRef, useState } from "react";
import { Box, Typography, Fade, Tooltip, useMediaQuery } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import {
  ArrowUpRight,
  ArrowDownLeft,
  ArrowLeftRight,
  Eye,
  CheckCheck,
  AlertTriangle,
  Users,
  Send,
} from "lucide-react";

const useCountUp = (target, duration = 800) => {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  const rafRef = useRef(null);

  useEffect(() => {
    if (target === fromRef.current) return;
    cancelAnimationFrame(rafRef.current);
    const from = fromRef.current;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(from + (target - from) * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else fromRef.current = target;
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return value;
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    card: {
      position: "relative",
      borderRadius: 18,
      padding: "20px 22px 16px",
      overflow: "hidden",
      background: dark
        ? "linear-gradient(145deg, rgba(18,18,19,0.85) 0%, rgba(26,26,28,0.85) 100%)"
        : "#ffffff",
      border: dark
        ? "1px solid rgba(91,124,255,0.14)"
        : "1px solid #e2e8f0",
      boxShadow: dark
        ? "0 8px 32px rgba(0,0,0,0.4)"
        : "0 4px 6px rgba(0,0,0,0.05)",
      transition: "transform 220ms ease, box-shadow 220ms ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: dark
          ? "0 14px 40px rgba(0,0,0,0.5)"
          : "0 8px 16px rgba(0,0,0,0.08)",
      },
    },
    sparkline: {
      position: "absolute",
      inset: 0,
      pointerEvents: "none",
      opacity: 0.55,
    },
    label: {
      position: "relative",
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.5)" : "#64748b",
      display: "flex",
      alignItems: "center",
      gap: 8,
    },
    icon: {
      width: 24,
      height: 24,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 8,
      flexShrink: 0,
    },
    value: {
      position: "relative",
      fontSize: "2.2rem",
      fontWeight: 700,
      letterSpacing: "-0.02em",
      lineHeight: 1.05,
      marginTop: 12,
      color: dark ? "#ffffff" : "#0f172a",
    },
    trend: {
      position: "relative",
      marginTop: 6,
      fontSize: 11,
      color: dark ? "rgba(255,255,255,0.42)" : "#94a3b8",
      letterSpacing: "0.02em",
    },
    subtitle: {
      position: "relative",
      marginTop: 4,
      fontSize: 11,
      color: dark ? "rgba(255,255,255,0.55)" : "#64748b",
      letterSpacing: "0.02em",
      fontVariantNumeric: "tabular-nums",
    },
    accentLine: {
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 3,
      borderRadius: "0 3px 3px 0",
    },
    helpIcon: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 14,
      height: 14,
      borderRadius: "50%",
      border: dark
        ? "1px solid rgba(255,255,255,0.25)"
        : "1px solid rgba(148,163,184,0.55)",
      color: dark ? "rgba(255,255,255,0.55)" : "#64748b",
      fontSize: 9,
      fontWeight: 700,
      cursor: "help",
      marginLeft: 4,
      flexShrink: 0,
      letterSpacing: 0,
    },
  };
});

const VARIANTS = {
  sent: {
    Icon: ArrowUpRight,
    iconColor: "#a78bfa",
    bg: "linear-gradient(135deg, rgba(167,139,250,0.18), rgba(236,72,153,0.18))",
    accent: "linear-gradient(180deg, #a78bfa 0%, #ec4899 100%)",
    chartColor: "#a78bfa",
    chartGradient: ["#a78bfa", "#ec4899"],
  },
  received: {
    Icon: ArrowDownLeft,
    iconColor: "#22d3ee",
    bg: "linear-gradient(135deg, rgba(34,211,238,0.16), rgba(20,184,166,0.16))",
    accent: "linear-gradient(180deg, #22d3ee 0%, #14b8a6 100%)",
    chartColor: "#22d3ee",
    chartGradient: ["#22d3ee", "#14b8a6"],
  },
  total: {
    Icon: ArrowLeftRight,
    iconColor: "#94a3b8",
    bg: "linear-gradient(135deg, rgba(148,163,184,0.12), rgba(100,116,139,0.12))",
    accent: "linear-gradient(180deg, #94a3b8 0%, #64748b 100%)",
    chartColor: "#94a3b8",
    chartGradient: ["#94a3b8", "#64748b"],
  },
  read: {
    Icon: Eye,
    iconColor: "#60a5fa",
    bg: "linear-gradient(135deg, rgba(96,165,250,0.16), rgba(59,130,246,0.16))",
    accent: "linear-gradient(180deg, #60a5fa 0%, #3b82f6 100%)",
    chartColor: "#60a5fa",
    chartGradient: ["#60a5fa", "#3b82f6"],
  },
  delivered: {
    Icon: CheckCheck,
    iconColor: "#34d399",
    bg: "linear-gradient(135deg, rgba(52,211,153,0.16), rgba(16,185,129,0.16))",
    accent: "linear-gradient(180deg, #34d399 0%, #10b981 100%)",
    chartColor: "#34d399",
    chartGradient: ["#34d399", "#10b981"],
  },
  failed: {
    Icon: AlertTriangle,
    iconColor: "#f87171",
    bg: "linear-gradient(135deg, rgba(248,113,113,0.16), rgba(239,68,68,0.16))",
    accent: "linear-gradient(180deg, #f87171 0%, #ef4444 100%)",
    chartColor: "#f87171",
    chartGradient: ["#f87171", "#ef4444"],
  },
  unique: {
    Icon: Users,
    iconColor: "#fbbf24",
    bg: "linear-gradient(135deg, rgba(251,191,36,0.16), rgba(245,158,11,0.16))",
    accent: "linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%)",
    chartColor: "#fbbf24",
    chartGradient: ["#fbbf24", "#f59e0b"],
  },
  templates: {
    Icon: Send,
    iconColor: "#818cf8",
    bg: "linear-gradient(135deg, rgba(129,140,248,0.16), rgba(99,102,241,0.16))",
    accent: "linear-gradient(180deg, #818cf8 0%, #6366f1 100%)",
    chartColor: "#818cf8",
    chartGradient: ["#818cf8", "#6366f1"],
  },
};

const formatValue = (value, format) => {
  if (format === "percent") return `${value.toFixed(1)}%`;
  return Math.round(value).toLocaleString("pt-BR");
};

const MetricCard = ({
  label,
  value,
  variant = "total",
  sparkData = [],
  delay = 0,
  format = "number",
  subtitle,
  tooltip,
  periodDays,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const v = VARIANTS[variant] || VARIANTS.total;
  const gradId = `mc-grad-${variant}`;
  const animated = useCountUp(value || 0, 800);

  const data = useMemo(
    () => sparkData.map((d, i) => ({ x: i, y: d })),
    [sparkData]
  );

  // Mobile: sparkline ocupa o card inteiro e prejudica leitura do número.
  // Esconde abaixo de sm; opcionalmente atenua via opacity em telas reduzidas.
  const showSpark = !isMobile && data.length > 1;

  return (
    <Fade in timeout={500} style={{ transitionDelay: `${delay}ms` }}>
      <Box className={classes.card} style={{ background: v.bg, backgroundColor: undefined }}>
        <Box className={classes.accentLine} style={{ background: v.accent }} />
        {showSpark && (
          <Box className={classes.sparkline}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 30, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={v.chartGradient[0]} stopOpacity={0.45} />
                    <stop offset="100%" stopColor={v.chartGradient[1]} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="y"
                  stroke={v.chartColor}
                  strokeWidth={1.6}
                  fill={`url(#${gradId})`}
                  isAnimationActive
                  animationDuration={700}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        )}
        <Typography component="div" className={classes.label}>
          <span className={classes.icon} style={{ color: v.iconColor }}>
            <v.Icon size={16} strokeWidth={2} />
          </span>
          {label}
          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top" interactive>
              <span className={classes.helpIcon}>?</span>
            </Tooltip>
          )}
        </Typography>
        <Typography component="div" className={classes.value}>
          {formatValue(animated, format)}
        </Typography>
        {subtitle && (
          <Typography component="div" className={classes.subtitle}>
            {subtitle}
          </Typography>
        )}
        {!subtitle && sparkData.length > 1 && (
          <Typography component="div" className={classes.trend}>
            {/* periodDays vem do range real selecionado no filtro (startDate/endDate).
                Antes usávamos sparkData.length, que só conta dias COM atividade —
                em um range de 90d sem msg em 86 deles virava "últimos 4 dias". */}
            últimos {periodDays || sparkData.length} dias
          </Typography>
        )}
      </Box>
    </Fade>
  );
};

export default MetricCard;
